import * as React from 'react'
// libs
import styled from 'styled-components'
import { defaultTheme, GroupBase } from 'react-select'
// icons
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
// types
import { Theme } from '@mui/material'
import { IOption } from 'components/FormElements/Select/index'
import type { StylesConfig } from 'react-select'
// theme
import { theme } from 'themes'
import colors from 'themes/colors'
// utils
import { get } from 'lodash'
import { safeGetOr } from 'utils/fp'

export interface ISelectStylesProps {
  theme?: Theme
  isError: boolean
  isFocused: boolean
  isSuccess?: boolean
  overrides?: Record<string, any>
  isDisabled: boolean
  isMultiple?: boolean
  hasInputValue: boolean
}

const MAIN_COLOR = theme.palette.primary.main
const SUCCESS_COLOR = theme.palette.primary.main
const ERROR_COLOR = colors.red300

export type Indent = Partial<{ top: number; bottom: number; left: number; right: number }>

const SelectContainer = styled('div')<{ indent?: Indent }>`
  padding-top: ${({ indent }) => get(indent, 'top', 0)}px;
  padding-bottom: ${({ indent }) => get(indent, 'bottom', 0)}px;
  padding-left: ${({ indent }) => get(indent, 'left', 0)}px;
  padding-right: ${({ indent }) => get(indent, 'right', 0)}px;

  .MuiFormLabel-root {
    padding-right: 35px;
  }
`

const SelectWrapper = styled('div')<{ $isLabelExist: boolean; $withoutDropDown: boolean }>`
  position: relative;
  margin-top: ${({ $isLabelExist }) => ($isLabelExist ? 16 : 0)}px;

  .react-select__input-container {
    height: 32px;
  }

  svg {
    display: ${({ $withoutDropDown }) => ($withoutDropDown ? 'none' : 'block')};
  }
`

const Icon = styled(({ isDisabled, ...rest }) => <ArrowDropDown {...rest} />)<{ isDisabled: boolean }>`
  && {
    position: absolute;
    top: 50%;
    right: 5px;
    transform: translateY(-50%);
    pointer-events: none;
    fill: ${({ isDisabled }) => (isDisabled ? 'rgba(0, 0, 0, 0.38)' : 'rgba(0, 0, 0, 0.51)')};
  }
`

export const reactSelectTheme = {
  ...defaultTheme,
  colors: {
    ...defaultTheme.colors,
    primary: defaultTheme.colors.neutral20,
    primary75: defaultTheme.colors.neutral20,
    primary50: defaultTheme.colors.neutral20,
    primary25: defaultTheme.colors.neutral20
  }
}

const getDefaultSelectStyles = ({
  theme,
  isError,
  isSuccess,
  isFocused,
  overrides,
  isDisabled,
  isMultiple,
  hasInputValue
}: ISelectStylesProps): StylesConfig<IOption, false, GroupBase<IOption>> => {
  const borderBottom = safeGetOr<any, any, any>(
    'rgba(0, 0, 0, 0.42)',
    'overrides.MuiInput.underline["&::before"].borderBottomColor'
  )(theme)

  return {
    valueContainer: (styles) => ({
      ...styles,
      padding: 0,
      fontSize: 16,
      gap: 4,
      minHeight: 32,
      paddingBottom: isMultiple ? 4 : 0
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: colors.gray290,
      borderRadius: '16px',
      overflow: 'hidden',
      alignItems: 'center',
      margin: 0,
      minWidth: 34,
      minHeight: 24
    }),
    multiValueLabel: () => ({
      padding: '2px 8px',
      color: colors.gray450,
      fontSize: '14px',
      overflowWrap: 'break-word',
      whiteSpace: 'pre-wrap'
    }),
    input: (styles) => ({
      ...styles,
      overflow: 'hidden',
      position: hasInputValue ? 'initial' : 'absolute',
      display: isDisabled ? 'none' : 'block',
      zIndex: hasInputValue ? 1 : -1
    }),
    multiValueRemove: () => ({
      display: isDisabled ? 'none' : 'flex',
      alignItems: 'center',
      backgroundColor: 'rgba(0,0,0,0.25)',
      color: '#efefef',
      transition: 'background-color .1s ease-in-out',
      borderRadius: '50%',
      margin: '0 4px',
      justifyContent: 'center',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.2)'
      }
    }),
    placeholder: (styles) => ({
      ...styles,
      padding: 0,
      margin: 0,
      display: isFocused ? 'block' : 'none',
      color: '#a1a1a1',
      fontWeight: 'lighter',
      paddingLeft: '2px'
    }),
    control: (styles) => ({
      ...styles,
      position: 'relative',
      backgroundColor: 'transparent',
      borderRadius: 0,
      outline: 'none',
      border: 'none',
      boxShadow: 'none',
      minHeight: '32px',
      paddingRight: isDisabled ? 0 : '35px',
      cursor: isDisabled ? 'default' : 'pointer',
      ':before': {
        content: '" "',
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        transition: 'border-bottom-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        borderBottom: `${isError ? 2 : 1}px ${isDisabled ? 'dotted' : 'solid'} ${
          isError ? ERROR_COLOR : isSuccess ? SUCCESS_COLOR : borderBottom
        }`
      },
      ':after': {
        content: '" "',
        left: 0,
        right: 0,
        bottom: 0,
        position: 'absolute',
        transform: `scaleX(${isFocused ? 1 : 0})`,
        transition: 'transform 200ms cubic-bezier(0, 0, 0.2, 1) 0ms',
        borderBottom: `2px solid ${isError ? ERROR_COLOR : isSuccess ? SUCCESS_COLOR : MAIN_COLOR}`,
        pointerEvents: 'none'
      },
      '&:hover:before': isDisabled
        ? {}
        : {
            borderBottom: `2px solid ${isError ? ERROR_COLOR : 'rgba(0, 0, 0, 0.87)'}`
          }
    }),
    menu: (styles) => {
      const updatedStyles = {
        ...styles,
        padding: '8px 0',
        margin: 0,
        background: colors.white100,
        borderRadius: '4px',
        zIndex: 50,
        boxShadow:
          '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)'
      }

      if (styles.top) {
        updatedStyles.top = 'calc(100% + 3px)'
      }

      return updatedStyles
    },
    option: (styles, options) => ({
      ...styles,
      minHeight: '46px',
      padding: '11px 16px',
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: '1.5em',
      cursor: 'pointer',
      color: options.isDisabled ? 'lightgray' : 'black',
      marginBottom: '8px',
      background: options.isFocused ? 'rgba(0, 0, 0, 0.14)' : 'transparent',
      '&:hover': options.isDisabled && {
        background: 'transparent',
        cursor: 'unset'
      }
    }),
    indicatorsContainer: () => ({
      display: 'none'
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    ...overrides
  }
}

export { SelectContainer, getDefaultSelectStyles, SelectWrapper, Icon }
